import React from 'react';
import { Link } from 'react-router-dom';
import { Navigation } from '../components/Navigation';
import { Footer } from '../components/Footer';
import { ArrowRight } from 'lucide-react';
export function CPGLandingPage() {
  return <div className="min-h-screen w-full bg-white selection:bg-[#0066FF] selection:text-white" data-id="element-2283">
      {/* Minimal Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-md border-b border-gray-100" data-id="element-2284">
        <div className="w-full px-6 md:px-12 lg:px-24" data-id="element-2285">
          <div className="max-w-7xl mx-auto h-20 flex items-center justify-between" data-id="element-2286">
            <Link to="/" data-id="element-2287">
              <img src="https://cdn.magicpatterns.com/uploads/9qJGNjQNSV94689CCZT1kr/newTM_black_copy.png" alt="Synthia" className="h-8" data-id="element-2288" />
            </Link>

            {/* Simplified Navigation */}
            <nav className="hidden md:flex items-center gap-8 text-sm font-medium text-gray-600" data-id="element-2289">
              <Link to="/the-instruction-layer" className="hover:text-black transition-colors" data-id="element-2290">
                Platform
              </Link>
              <Link to="/context-engineering" className="hover:text-black transition-colors" data-id="element-2291">
                Solutions
              </Link>
              <Link to="/pricing" className="hover:text-black transition-colors" data-id="element-2292">
                Pricing
              </Link>
              <Link to="/contact" className="bg-black text-white px-6 py-2.5 font-['Syne'] font-bold text-xs tracking-wide hover:bg-[#0066FF] transition-colors" data-id="element-2293">
                REQUEST DEMO
              </Link>
            </nav>
          </div>
        </div>
      </header>

      <main className="pt-20" data-id="element-2294">
        {/* Hero Section */}
        <section className="py-24 px-6 md:px-12 lg:px-24 bg-white" data-id="element-2295">
          <div className="max-w-6xl mx-auto text-center" data-id="element-2296">
            <div className="inline-flex items-center px-3 py-1 rounded-full border border-gray-200 bg-gray-50 mb-8" data-id="element-2297">
              <span className="text-xs font-medium text-gray-600 uppercase tracking-wider font-sans" data-id="element-2298">
                CONTEXT PROVIDER FOR AI-DRIVEN CPG CONTENT
              </span>
            </div>

            <h1 className="text-5xl md:text-7xl font-bold leading-tight mb-8 font-['Syne']" data-id="element-2299">
              Your Brand Voice,{' '}
              <span className="bg-[#0066FF] text-white px-3" data-id="element-2300">Every</span> Time.
            </h1>

            <p className="text-xl text-gray-600 leading-relaxed max-w-4xl mx-auto mb-12" data-id="element-2301">
              CPG teams spend too much time re-prompting AI to sound like their
              brand. MeetSynthia sits between your team and any LLM —
              automatically applying your brand guardrails, compliance rules,
              and channel standards so every output is right the first time.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-2302">
              <Link to="/contact" className="group inline-flex items-center justify-center px-8 py-4 text-lg font-bold text-white bg-[#0066FF] font-['Syne'] hover:bg-black transition-all duration-200" data-id="element-2303">
                BOOK A DEMO
                <ArrowRight className="ml-2 w-5 h-5 transition-transform group-hover:translate-x-1" data-id="element-2304" />
              </Link>
              <Link to="/how-it-works" className="group inline-flex items-center justify-center px-8 py-4 text-lg font-bold text-[#0066FF] font-['Syne'] hover:text-black transition-colors" data-id="element-2305">
                See How It Works
                <ArrowRight className="ml-2 w-5 h-5 transition-transform group-hover:translate-x-1" data-id="element-2306" />
              </Link>
            </div>
          </div>
        </section>

        {/* Context Section */}
        <section className="py-24 px-6 md:px-12 lg:px-24 bg-gray-50" data-id="element-2307">
          <div className="max-w-7xl mx-auto" data-id="element-2308">
            <div className="text-center mb-16" data-id="element-2309">
              <span className="text-sm font-bold tracking-widest uppercase text-[#0066FF] mb-4 block" data-id="element-2310">
                THE CONTEXT YOUR AI HAS BEEN MISSING
              </span>
              <h2 className="text-4xl md:text-5xl font-bold mb-6 font-['Syne']" data-id="element-2311">
                AI that finally sounds like your brand
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto" data-id="element-2312">
                LLMs don't know your brand. Synthia does. Before any output
                reaches your team, Synthia injects the context, guardrails, and
                standards that make it yours.
              </p>
            </div>

            {/* Feature Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-2313">
              {/* Brand Consistency */}
              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-id="element-2314">
                <div className="p-6" data-id="element-2315">
                  <img src="https://cdn.magicpatterns.com/uploads/4HGKq3EG8Gi6ieCQ5ynRNP/69c2c0872b71e658f59fce31_bk.png" alt="Brand Consistency Interface" className="w-full h-auto mb-6 rounded" data-id="element-2316" />
                  <h3 className="text-2xl font-bold mb-4 font-['Syne']" data-id="element-2317">
                    Brand Consistency
                  </h3>
                  <p className="text-gray-600 leading-relaxed" data-id="element-2318">
                    Your BrandKey guardrail encodes voice, positioning, and
                    personality across 17 attributes. Every piece of
                    AI-generated copy — from campaign headlines to retailer sell
                    sheets — sounds like you wrote it.
                  </p>
                </div>
              </div>

              {/* Faster Content Production */}
              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-id="element-2319">
                <div className="p-6" data-id="element-2320">
                  <img src="https://cdn.magicpatterns.com/uploads/jYYrRiJqhQBnVH7EhaxHA4/69c2bd5849923cd7e3353667_Screenshot_2026-03-24_at_12.35.11_PM.png" alt="Faster Content Production Interface" className="w-full h-auto mb-6 rounded" data-id="element-2321" />
                  <h3 className="text-2xl font-bold mb-4 font-['Syne']" data-id="element-2322">
                    Faster Content Production
                  </h3>
                  <p className="text-gray-600 leading-relaxed" data-id="element-2323">
                    Presets bundle the right guardrails for your most common CPG
                    workflows — new product launches, seasonal promotions, trade
                    deck updates. One click, and the LLM already has everything
                    it needs.
                  </p>
                </div>
              </div>

              {/* Built-In Compliance */}
              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-id="element-2324">
                <div className="p-6" data-id="element-2325">
                  <img src="https://cdn.magicpatterns.com/uploads/p7JRVMHraLrhyyRoxVumuZ/69c2bf879738ca46229ead79_flags.png" alt="Built-In Compliance Interface" className="w-full h-auto mb-6 rounded" data-id="element-2326" />
                  <h3 className="text-2xl font-bold mb-4 font-['Syne']" data-id="element-2327">
                    Built-In Compliance
                  </h3>
                  <p className="text-gray-600 leading-relaxed" data-id="element-2328">
                    Regulatory and channel guardrails — FTC
                    truth-in-advertising, FDA claim accuracy, retailer content
                    standards — keep every output within the lines before it
                    ever reaches a reviewer.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Pilot Program CTA */}
        <section className="py-24 px-6 md:px-12 lg:px-24 bg-[#0A1628] text-white" data-id="element-2329">
          <div className="max-w-4xl mx-auto text-center" data-id="element-2330">
            <div className="inline-flex items-center px-4 py-2 rounded-full border border-white/20 bg-white/10 mb-8" data-id="element-2331">
              <span className="text-xs font-medium uppercase tracking-wider" data-id="element-2332">
                PILOT PROGRAM
              </span>
            </div>

            <h2 className="text-4xl md:text-5xl font-bold mb-8 font-['Syne']" data-id="element-2333">
              Start in days, not months.
            </h2>

            <p className="text-xl text-gray-300 leading-relaxed mb-12 max-w-3xl mx-auto" data-id="element-2334">
              We'll configure Synthia around your brand and your team's most
              common workflows. Use it for 30 days — then we map the full
              picture together. No lengthy implementation, no IT project.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-2335">
              <Link to="/contact" className="group inline-flex items-center justify-center px-8 py-4 text-lg font-bold text-white bg-[#0066FF] font-['Syne'] hover:bg-white hover:text-black transition-all duration-200" data-id="element-2336">
                START YOUR PILOT
                <ArrowRight className="ml-2 w-5 h-5 transition-transform group-hover:translate-x-1" data-id="element-2337" />
              </Link>
              <Link to="/contact" className="group inline-flex items-center justify-center px-8 py-4 text-lg font-bold text-white font-['Syne'] hover:text-[#0066FF] transition-colors" data-id="element-2338">
                Talk to Us
                <ArrowRight className="ml-2 w-5 h-5 transition-transform group-hover:translate-x-1" data-id="element-2339" />
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer data-id="element-2340" />
    </div>;
}